import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Alert,
  Chip,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import { SectorSelect, type Sector } from './common/SectorSelect';
import InfoIcon from '@mui/icons-material/Info';
import BusinessIcon from '@mui/icons-material/Tag';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { useAuth, LandingFormData } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import { trackTrialSignup } from '../utils/analytics';
import { getCurrentTOSVersion } from '../utils/termsVersion';
import { csrfService } from '../services/csrf';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  expressMode?: boolean;
  landingFormData?: LandingFormData | null;
}

const OnboardingModal: React.FC<OnboardingModalProps> = ({ isOpen, onClose, expressMode = false, landingFormData }) => {
  const { user, updateUser, inactiveUser, token, refreshUserData, setShowOnboarding } = useAuth();
  const navigate = useNavigate();

  // Flag to prevent double navigation
  const [hasNavigated, setHasNavigated] = useState(false);
  const sectorResolutionCache = useRef<Map<string, Sector>>(new Map());

  const resolveSelectedSector = useCallback(async (sector: Sector | null) => {
    if (!sector) {
      return { sectorId: undefined as string | undefined, sectorName: '', resolvedSector: null as Sector | null };
    }

    const cacheKeyCandidates = [
      sector.id,
      sector.slug ? `slug:${sector.slug}` : null,
      sector.name ? `name:${sector.name.toLowerCase()}` : null
    ].filter(Boolean) as string[];

    const registerCache = (resolved: Sector) => {
      const cache = sectorResolutionCache.current;
      cache.set(resolved.id, resolved);
      if (resolved.slug) {
        cache.set(`slug:${resolved.slug}`, resolved);
      }
      if (resolved.name) {
        cache.set(`name:${resolved.name.toLowerCase()}`, resolved);
      }
    };

    for (const key of cacheKeyCandidates) {
      const cached = sectorResolutionCache.current.get(key);
      if (cached) {
        return { sectorId: cached.id, sectorName: cached.name, resolvedSector: cached };
      }
    }

    if (sector.id && sector.id.startsWith('cm')) {
      registerCache(sector);
      return { sectorId: sector.id, sectorName: sector.name, resolvedSector: sector };
    }

    const query = sector.name?.trim();
    if (!query) {
      return { sectorId: undefined, sectorName: sector.name || '', resolvedSector: sector };
    }

    try {
      const config: Record<string, any> = {
        params: { query },
        withCredentials: true // Use cookies instead of Authorization header
      };

      const response = await axios.get(`${API_URL}/api/v1/sectors/search`, config);

      const sectors: Sector[] = response.data?.sectors || [];
      const matched = sectors.find((candidate) => {
        if (sector.slug && candidate.slug === sector.slug) {
          return true;
        }
        return candidate.name.toLowerCase() === query.toLowerCase();
      }) || sectors[0];

      if (matched) {
        registerCache(matched);
        return { sectorId: matched.id, sectorName: matched.name, resolvedSector: matched };
      }

      logger.warn('Sector resolution fallback: no exact match found for selection', {
        query,
        provided: {
          id: sector.id,
          slug: sector.slug,
          name: sector.name
        },
        receivedCount: sectors.length
      });
    } catch (error) {
      logger.warn('Failed to resolve sector ID from selection', error);
    }

    return { sectorId: undefined, sectorName: sector.name || '', resolvedSector: sector };
  }, [token]);

  // Log the props for debugging
  useEffect(() => {
    logger.info('OnboardingModal mounted/updated:', {
      isOpen,
      expressMode,
      hasLandingFormData: !!landingFormData,
      landingFormData,
      hasNavigated
    });

    // Reset navigation flag when modal opens
    if (isOpen && hasNavigated) {
      setHasNavigated(false);
    }
  }, [isOpen, expressMode, landingFormData, hasNavigated]);

  // Log when unmounting and track navigation state
  useEffect(() => {
    return () => {
      logger.info('OnboardingModal unmounting', {
        hasNavigated,
        expressMode,
        hadLandingData: !!landingFormData
      });
    };
  }, [hasNavigated, expressMode, landingFormData]);

  // In express mode, skip account creation step
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // Pre-fill form data if coming from landing page
  const getInitialFormData = () => {
    const base = {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      organizationName: '',
      accountName: '',
      accountDescription: '',
      accountSector: '',
      accountSectorObj: null as Sector | null,
    };
    
    if (landingFormData?.data) {
      const { brand, brandDescription, sector, sectorId } = landingFormData.data;
      return {
        ...base,
        organizationName: brand || '',
        accountName: brand || '',
        accountDescription: brandDescription || '',
        accountSector: sector || '',
        // If we have a sectorId, create a minimal sector object for express onboarding
        accountSectorObj: sectorId ? { id: sectorId, name: sector } as Sector : null,
      };
    }
    
    return base;
  };
  
  const [formData, setFormData] = useState(getInitialFormData());

  // In express mode, only show organization setup (account will be created automatically)
  const steps = expressMode ? ['Set Up Organization'] : ['Set Up Organization', 'Create Your First Account'];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validate step 1
      if (!formData.firstName || !formData.lastName || !formData.organizationName) {
        setError('Please fill out all fields');
        return;
      }
      // Also validate terms acceptance on first step
      if (!acceptedTerms) {
        setError('Please accept the Terms of Service and Privacy Policy');
        return;
      }
    }
    setError(null);
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError(null);
    setActiveStep((prevStep) => prevStep - 1);
  };

  const getCsrfHeaders = async (): Promise<Record<string, string>> => {
    const headers = await csrfService.addTokenToHeaders<Record<string, string>>({});
    return headers;
  };

  const handleSubmit = async () => {
    // Log the initial state for debugging
    logger.info('OnboardingModal handleSubmit called:', {
      expressMode,
      hasLandingFormData: !!landingFormData,
      landingFormDataMode: landingFormData?.mode,
      landingFormDataKeys: landingFormData?.data ? Object.keys(landingFormData.data) : [],
      hasNavigated,
      formData: {
        organizationName: formData.organizationName,
        accountName: formData.accountName,
        sector: formData.accountSector
      }
    });

    // CRITICAL: Capture props immediately before any state changes or async operations
    const capturedLandingFormData = landingFormData;
    const capturedExpressMode = expressMode;

    logger.info('Captured at submit start:', {
      expressMode: capturedExpressMode,
      hasLandingData: !!capturedLandingFormData,
      mode: capturedLandingFormData?.mode
    });

    // Validate organization fields
    if (!formData.firstName || !formData.lastName || !formData.organizationName) {
      setError('Please fill out all organization fields');
      return;
    }

    // Validate terms acceptance
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy');
      return;
    }

    // In non-express mode, also validate account fields
    if (!capturedExpressMode && (!formData.accountName || !formData.accountSectorObj)) {
      setError('Please fill out all account fields');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { sectorId: resolvedSectorId, sectorName: resolvedSectorName, resolvedSector } = await resolveSelectedSector(formData.accountSectorObj);

      if (resolvedSector && (!formData.accountSectorObj || resolvedSector.id !== formData.accountSectorObj.id)) {
        setFormData((prev) => ({
          ...prev,
          accountSectorObj: resolvedSector,
          accountSector: resolvedSectorName || prev.accountSector
        }));
      }

      const effectiveAccountName = formData.accountName || formData.organizationName;
      const effectiveSectorName = resolvedSectorName || formData.accountSectorObj?.name || formData.accountSector;
      const normalizedSectorName = effectiveSectorName?.trim() || undefined;

      let orgResponse;
      
      // Use express onboarding endpoint if in express mode and have account data
      if (capturedExpressMode && effectiveAccountName && (normalizedSectorName || resolvedSectorId)) {
        const endpoint = `${API_URL}/api/v1/user/express-onboarding`;

        logger.info(`Using express onboarding endpoint with:`, {
          accountName: effectiveAccountName,
          sector: normalizedSectorName,
          sectorId: resolvedSectorId
        });

        // Using cookie-based auth, no need to set Authorization header
        const headers = await getCsrfHeaders();

        orgResponse = await axios.post(endpoint, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          organizationName: formData.organizationName,
          accountName: effectiveAccountName.trim(),
          accountDescription: formData.accountDescription?.trim() || undefined,
          accountSectorId: resolvedSectorId,
          accountSector: normalizedSectorName || formData.accountSector?.trim(),
          termsVersion: getCurrentTOSVersion()
        }, {
          withCredentials: true, // Use cookies instead of Authorization header
          headers
        });
      } else {
        // Traditional flow: Create organization first
        const endpoint = inactiveUser 
          ? `${API_URL}/api/user/setup-organization-inactive` 
          : `${API_URL}/api/user/setup-organization`;
        
        logger.info(`Using endpoint: ${endpoint} (inactiveUser: ${inactiveUser})`);
        
        // Using cookie-based auth, no need to set Authorization header
        const headers = await getCsrfHeaders();

        orgResponse = await axios.post(endpoint, {
          firstName: formData.firstName,
          lastName: formData.lastName,
          organizationName: formData.organizationName,
          termsVersion: getCurrentTOSVersion()
        }, {
          withCredentials: true, // Use cookies instead of Authorization header
          headers
        });

        // Handle the new token
        // Token is already set in httpOnly cookies by backend
        // No need to store in localStorage or set Authorization header

        // Step 2: Create the first account (if we have the data and not in express mode)
        if (!capturedExpressMode && formData.accountName && formData.accountSectorObj) {
          try {
            logger.info('Creating account with:', {
              name: formData.accountName,
              sectorId: resolvedSectorId,
              sector: normalizedSectorName
            });

            // IMPORTANT: Use the new token from orgResponse which includes organizationId
            const authToken = orgResponse.data.token || token;
            const accountPayload: Record<string, any> = {
              name: formData.accountName.trim(),
              description: formData.accountDescription?.trim() || undefined
            };

            if (resolvedSectorId) {
              accountPayload.sectorId = resolvedSectorId;
            }

            if (!resolvedSectorId && normalizedSectorName) {
              accountPayload.sector = normalizedSectorName;
            }

            const accountHeaders = await getCsrfHeaders();

            await axios.post(`${API_URL}/api/v1/accounts`, accountPayload, {
              withCredentials: true, // Use cookies instead of Authorization header
              headers: accountHeaders
            });

            logger.info('Account created successfully');
          } catch (accountError: any) {
            logger.error('Error creating account:', {
              error: accountError,
              response: accountError.response?.data,
              status: accountError.response?.status
            });
            // Don't fail the entire onboarding if account creation fails
            setError('Organization created successfully, but there was an issue creating your account. You can create one in Settings.');
          }
        }
      }

      // Update user context with the new information
      if (orgResponse.data.user) {
        updateUser(orgResponse.data.user);
      }
      
      // Token is already set in httpOnly cookies by backend
      // No need to store in localStorage or set Authorization header

      // Refresh user data to ensure TOS fields are up to date
      await refreshUserData();

      // Track GA4 conversion for trial signup
      if (orgResponse.data.user && orgResponse.data.organization) {
        trackTrialSignup(
          orgResponse.data.user.email, 
          orgResponse.data.organization.name
        );
      }

      // Prevent double navigation
      if (hasNavigated) {
        logger.warn('Navigation already triggered, skipping duplicate navigation');
        return;
      }

      // Set flag to prevent double navigation
      setHasNavigated(true);

      // Handle navigation based on mode and landing form data - use CAPTURED values
      if (capturedExpressMode && capturedLandingFormData && capturedLandingFormData.data) {
        logger.info('Express mode navigation with landing data:', {
          mode: capturedLandingFormData.mode,
          hasFormData: !!capturedLandingFormData.data,
          dataKeys: Object.keys(capturedLandingFormData.data || {})
        });

        // DON'T call onClose() here - let navigation happen first
        // The modal will be unmounted when we navigate away from DashboardPage

        // Store form data in sessionStorage as a fallback (in case route state gets lost)
        const navigationData = {
          fromLanding: true,
          mode: capturedLandingFormData.mode,
          formData: capturedLandingFormData.data
        };
        sessionStorage.setItem('landing_form_navigation', JSON.stringify(navigationData));
        logger.info('Stored navigation data in sessionStorage:', navigationData);

        const finalizeOnboarding = () => {
          // Delay closing so VerifyPage doesn't immediately redirect back to /dashboard
          setTimeout(() => setShowOnboarding(false), 0);
        };

        if (capturedLandingFormData.mode === 'ideas') {
          logger.info('Navigating to /ideas/create with state:', {
            state: navigationData,
            replace: true
          });

          // Navigate directly without setTimeout to preserve state
          navigate('/ideas/create', {
            state: navigationData,
            replace: true
          });

          finalizeOnboarding();
        } else {
          logger.info('Navigating to /simulations/create with state:', {
            state: navigationData,
            replace: true
          });

          // Navigate directly without setTimeout to preserve state
          navigate('/simulations/create', {
            state: navigationData,
            replace: true
          });

          finalizeOnboarding();
        }

      } else {
        logger.info('Standard navigation to dashboard:', {
          capturedExpressMode,
          hadCapturedData: !!capturedLandingFormData,
          hadDataField: !!capturedLandingFormData?.data
        });

        // Only call onClose when staying on dashboard page
        // This prevents race conditions with navigation
        navigate('/dashboard');
        // Delay closing to avoid VerifyPage redirect race conditions
        setTimeout(() => setShowOnboarding(false), 0);
        onClose();
      }
    } catch (error) {
      logger.error('Error setting up organization:', error);
      setError('There was an error setting up your organization.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Let's set up your account and organization.
            </Typography>
            
            <TextField
              label="First Name"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />
            
            <TextField
              label="Last Name"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />
            
            <TextField
              label="Organization Name"
              name="organizationName"
              value={formData.organizationName}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
            />

            <Divider sx={{ my: 2 }} />
            
            <Paper sx={(theme) => ({
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(10,124,160,0.12)' : theme.palette.info.light,
              p: 2,
              borderRadius: 1,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(10,124,160,0.35)' : 'rgba(0,0,0,0.08)'}`
            })}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Chip 
                  label="Trial Plan" 
                  color="primary" 
                  size="small" 
                  sx={{ mr: 1 }} 
                />
                <Typography fontWeight="bold" variant="body2">
                  Free 14-day trial
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                <InfoIcon fontSize="small" color="info" sx={{ mr: 1, mt: 0.5 }} />
                <Typography variant="body2">
                  Your organization will be created with a free trial plan that includes:
                </Typography>
              </Box>
              
              <List dense disablePadding sx={{ pl: 4 }}>
                <ListItem disableGutters>
                  <ListItemText primary="1 user seat" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="10 simulations per month" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="1 social account" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="14-day access" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemText primary="No credit card required" />
                </ListItem>
              </List>
            </Paper>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2">
                    I agree to the{' '}
                    <Link href="/legal/terms-of-service" target="_blank" rel="noopener">
                      Terms of Service
                    </Link>{' '}
                    and{' '}
                    <Link href="/legal/privacy-policy" target="_blank" rel="noopener">
                      Privacy Policy
                    </Link>
                  </Typography>
                }
              />
              {error && error.includes('Terms') && (
                <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, ml: 4 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BusinessIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="body1">
                Create your first social media account to start running simulations.
              </Typography>
            </Box>
            
            <TextField
              label="Account Name"
              name="accountName"
              value={formData.accountName}
              onChange={handleChange}
              fullWidth
              required
              margin="normal"
              placeholder="Acme Corp, John Smith, Our Brand"
              helperText="This is how the account will appear in your simulations"
            />
            
            <TextField
              label="Brand Description (Optional)"
              name="accountDescription"
              value={formData.accountDescription}
              onChange={handleChange}
              fullWidth
              margin="normal"
              multiline
              rows={3}
              placeholder="E.g., We're a sustainable fashion brand targeting Gen Z with eco-friendly streetwear"
              helperText="Provide a brief description of your brand to help us generate more accurate content"
              inputProps={{ maxLength: 500 }}
            />
            
            <SectorSelect
              value={formData.accountSectorObj}
              onChange={(sector) => setFormData(prev => ({ 
                ...prev, 
                accountSectorObj: sector,
                accountSector: sector?.name || ''
              }))}
              required
              fullWidth
              margin="normal"
              helperText="Select the industry sector for this account"
            />
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                You can create additional accounts later in Settings. Each account represents a different social media presence you want to test content for.
              </Typography>
            </Alert>
          </Box>
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          Welcome to WaveScoop
          {activeStep === 1 && <CheckCircleIcon color="success" />}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {renderStepContent(activeStep)}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button 
          disabled={activeStep === 0 || isLoading}
          onClick={handleBack}
        >
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {activeStep === steps.length - 1 ? (
          <Button 
            variant="contained" 
            disabled={isLoading}
            onClick={handleSubmit}
          >
            {isLoading ? 'Creating...' : 'Complete Setup'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={isLoading}
          >
            Next
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OnboardingModal; 
